/**
 * How long the "Item Added to Cart" confirmation stays up — live uses 1s.
 * component-cart-drawer.js holds the drawer back for the same span so the
 * confirmation is not buried by the drawer sliding over it; keep the two in step.
 */
const ADDED_STATE_DURATION = 1000;

/**
 * Width below which the gallery carousel is left to the CSS scroll-snap rules
 * instead of Swiper. Must stay in step with the matching media query in
 * section-product.css, and with the STACKED GALLERY LAYOUT rules there that
 * switch desktop/mobile at 768/769px.
 */
const GALLERY_MOBILE_QUERY = '(max-width: 768px)';

/**
 * Marker class on the Shopify section wrapper of product-context sections that
 * sit outside <product-info> (currently Pairs Well With). Set via the `class`
 * key in the section schema, so the wrapper is present even when the section
 * itself renders nothing.
 */
const COMPANION_SECTION_CLASS = 'product-companion-section';

export class ProductInfo extends HTMLElement {
  abortController = undefined;
  swiper = undefined;
  addedStateTimer = 0;
  #listenersBound = false;
  /** Every Swiper this element owns, so a re-init can tear all of them down. */
  #swipers = [];
  #galleryBreakpoint = undefined;
  /** Detaches the gallery scrollbar's scroll listener; set by initGalleryScrollbar. */
  #removeGalleryScroll = null;

  constructor() {
    super();
    this.onGalleryBreakpointChange = this.onGalleryBreakpointChange.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onNotifyTriggerClick = this.onNotifyTriggerClick.bind(this);
    this.onSizeErrorClick = this.onSizeErrorClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onQuantitySelectorEvent = this.onQuantitySelectorEvent.bind(this);
    this.onSiblingClick = this.onSiblingClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
  }

  setupEventListeners() {
    if (this.#listenersBound) return;
    this.#listenersBound = true;

    // Delegated rather than bound to <variant-selector> itself: a color swap
    // replaces that element wholesale, which would drop a direct listener.
    this.addEventListener('change', this.onVariantChange);
    if (this.quantitySelector) {
      this.quantitySelector.addEventListener('change', this.onQuantitySelectorEvent);
      this.quantitySelector.querySelector('button[name="plus"]')?.addEventListener('click', this.onQuantitySelectorEvent);
      this.quantitySelector.querySelector('button[name="minus"]')?.addEventListener('click', this.onQuantitySelectorEvent);
    }
    document.addEventListener('cart:change', this.onCartUpdate);
    this.addEventListener('click', this.onNotifyTriggerClick);
    this.addEventListener('click', this.onSiblingClick);
    this.addEventListener('click', this.onSizeErrorClick);
    window.addEventListener('popstate', this.onPopState);
  }

  connectedCallback() {
    this.setupEventListeners();

    // Set before initSwiper so `carouselIsNative` reads from the stored MQL on
    // first init instead of allocating another one.
    this.#galleryBreakpoint = window.matchMedia(GALLERY_MOBILE_QUERY);
    // Crossing the breakpoint swaps the carousel between Swiper (desktop) and
    // native scroll-snap (mobile), which is a different mechanism rather than a
    // resize — Swiper's own observer cannot handle it, so rebuild on change.
    this.#galleryBreakpoint.addEventListener('change', this.onGalleryBreakpointChange);

    this.initSwiper();

    // Seed history so back/forward between sibling color swaps has a handle to compare.
    if (this.dataset.updateUrl !== 'false' && window.location.pathname.includes('/products/')) {
      const currentHandle = window.location.pathname;
      if (!window.history.state?.productSiblingHandle) {
        window.history.replaceState({ ...(window.history.state || {}), productSiblingHandle: currentHandle }, '', window.location.href);
      }
      this.dataset.currentHandle = currentHandle;
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.removeEventListener('change', this.onVariantChange);
    document.removeEventListener('cart:change', this.onCartUpdate);
    this.removeEventListener('click', this.onNotifyTriggerClick);
    this.removeEventListener('click', this.onSiblingClick);
    this.removeEventListener('click', this.onSizeErrorClick);
    window.removeEventListener('popstate', this.onPopState);
    clearTimeout(this.addedStateTimer);
    this.#listenersBound = false;
    this.#galleryBreakpoint?.removeEventListener('change', this.onGalleryBreakpointChange);
    this.#galleryBreakpoint = undefined;
    this.#removeGalleryScroll?.();
    this.#removeGalleryScroll = null;
    this.destroySwipers();
  }

  /**
   * "Item Added to Cart" confirmation on the buy box button.
   *
   * Live parity: the buy box button carries no loader — nothing there ever adds
   * `is-loading`, and the only class its submit handler sets is `.atc__disable-1`
   * (pointer-events: none, custom.css). The label goes straight to the
   * confirmation, so this swaps it in and reverts after 1s.
   *
   * The drawer used to open on this same tick, which buried the confirmation
   * mid-fade — it slides in over this very column, so the add read as going
   * straight to the mini cart. component-cart-drawer.js now holds off for
   * ADDED_STATE_DURATION on buy box adds, which also lands closer to live's own
   * timing: its cart flyout appears about a second after the click, not instantly.
   *
   * @param {CustomEvent} event - cart.js 'cart:change'; detail.source is the form handed to Cart.add()
   * @returns {void}
   */
  onCartUpdate(event) {
    if (event.detail?.action !== 'add') return;

    const button = event.detail.source?.closest?.('.product-form__buttons')?.querySelector('button[name="add"]');
    if (!button || !this.contains(button)) return;

    button.classList.add('is-added');

    clearTimeout(this.addedStateTimer);
    this.addedStateTimer = setTimeout(() => button.classList.remove('is-added'), ADDED_STATE_DURATION);
  }

  /**
   * Back-in-stock trigger for the sold-out variant button. Delegated because the
   * button is re-rendered on every variant change (see updateSourceFromDestination).
   * Klaviyo binds its modal to the hidden `.klaviyo-bis-trigger` anchor rendered
   * by component-klaviyo-back-in-stock.liquid, which sits outside the container
   * that gets replaced, so forwarding the click keeps working after a swap. Live
   * reaches the modal the same way from its own sold-out button.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  onNotifyTriggerClick(event) {
    const trigger = event.target.closest('[data-notify-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();

    // Klaviyo sometimes re-renders the anchor rather than reusing ours, so fall
    // back to wherever it ended up. The visible button no longer carries the
    // class, so this can never loop back into itself.
    const klaviyoTrigger =
      this.querySelector('klaviyo-back-in-stock .klaviyo-bis-trigger') ||
      document.querySelector('.klaviyo-bis-trigger');

    klaviyoTrigger?.click();
  }

  /**
   * "Select a Size" placeholder click shows the pink nudge; its own close (X)
   * hides it again. Live parity (product-buttons.liquid / custom.js).
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  onSizeErrorClick(event) {
    if (event.target.closest('.product-form__placeholder')) {
      this.querySelector('[data-size-error]')?.style.setProperty('display', 'block');
      return;
    }

    if (event.target.closest('[data-size-error-close]')) {
      event.target.closest('[data-size-error]')?.style.setProperty('display', 'none');
    }
  }

  /**
   * Color sibling swatch click — live parity: section HTML swap, no full reload.
   * @param {MouseEvent} event
   * @returns {void}
   */
  onSiblingClick(event) {
    const link = event.target.closest('[data-sibling-swap]');
    if (!link || !this.contains(link)) return;

    // Already viewing this color — do nothing.
    if (link.classList.contains('product-siblings__swatch--active')) {
      event.preventDefault();
      return;
    }

    // Quick-add / modal contexts keep normal navigation.
    if (this.dataset.updateUrl === 'false') return;

    const productUrl = link.getAttribute('href');
    if (!productUrl) return;

    event.preventDefault();
    this.swapSiblingProduct(productUrl, { updateHistory: true });
  }

  /**
   * Browser back/forward between sibling color products.
   * @param {PopStateEvent} event
   * @returns {void}
   */
  onPopState(event) {
    if (this.dataset.updateUrl === 'false') return;
    if (!window.location.pathname.includes('/products/')) return;

    const targetHandle = window.location.pathname;
    if (this.dataset.currentHandle === targetHandle) return;

    // Only handle our sibling swaps (or any product URL while this PDP is mounted).
    this.swapSiblingProduct(targetHandle, { updateHistory: false });
  }

  /**
   * Fetch a sibling product's section HTML and patch the buy box in place.
   *
   * Live parity (product-swatch.js `swapProductSection`) — a color swap only
   * touches the gallery, the selectors, and the form's own data. Everything else
   * in the column is deliberately left alone: the short and long descriptions
   * stay put, and so does the Related products section. Only Pairs Well With is
   * refetched, because it is built from a per-product upsell metafield.
   *
   * The fresh render carries no ?variant=, so size comes back unselected.
   *
   * @param {string} productUrl
   * @param {{ updateHistory?: boolean }} [options]
   * @returns {Promise<void>}
   */
  async swapSiblingProduct(productUrl, { updateHistory = true } = {}) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const sectionId = this.dataset.section;
    this.style.pointerEvents = 'none';

    try {
      const response = await fetch(`${productUrl}?section_id=${sectionId}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) {
        window.location.href = productUrl;
        return;
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      if (!html.querySelector('product-info')) {
        window.location.href = productUrl;
        return;
      }

      this.destroySwipers();

      for (const id of [
        `ProductMedia-${sectionId}`,
        `ProductVariants-${sectionId}`,
        `price-${sectionId}`,
        `sku-${sectionId}`,
        `inventory-${sectionId}`,
        `AddToCartContainer-${sectionId}`,
      ]) {
        this.updateSourceFromDestination(html, id);
      }

      // The form posts this, and the new product's variant ids are different.
      const newVariantInput = html.querySelector(`#product-form-${sectionId} input[name="id"]`);
      this.updateVariantInputs(newVariantInput?.value || '');

      // Subsequent size picks must fetch against the color we are now showing.
      this.dataset.url = html.querySelector('product-info')?.dataset.url || productUrl;
      this.dataset.currentHandle = new URL(productUrl, window.location.origin).pathname;

      // Nothing is picked on the new color yet, so the slash starts hidden again.
      delete this.dataset.variantSelectionMade;

      this.initSwiper();

      if (updateHistory) {
        window.history.pushState({ productSiblingHandle: productUrl }, '', productUrl);
      }

      ProductInfo.refreshCompanionSections(productUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      window.location.href = productUrl;
    } finally {
      this.style.pointerEvents = '';
    }
  }

  /**
   * The carousel is Swiper-driven on desktop but a plain scroll container on
   * mobile, so crossing the breakpoint has to rebuild rather than re-measure.
   */
  onGalleryBreakpointChange() {
    this.initSwiper();
  }

  /** True while the carousel is left to the CSS scroll-snap rules. */
  get carouselIsNative() {
    return !!this.#galleryBreakpoint?.matches;
  }

  destroySwipers() {
    // Reverse order so a main gallery is torn down before the thumbs strip it
    // controls, rather than being left holding a destroyed instance.
    for (const instance of [...this.#swipers].reverse()) {
      instance?.destroy?.(true, true);
    }
    this.#swipers = [];
    this.swiper = undefined;
  }

  initSwiper() {
    // Re-entrant: a colour swap replaces the gallery markup and calls this again.
    // Without the teardown the old instances stayed bound to the detached nodes.
    this.destroySwipers();

    // Thumbnail + carousel layouts render both containers with CSS deciding
    // which is visible, so any Swiper attached to a hidden one needs to catch
    // up the moment its container appears. `observer` watches the target and
    // `observeParents` walks up for `display` toggles on ancestors.
    const recalculating = {
      observer: true,
      observeParents: true,
    };

    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    const thumbnailWrapperEl = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    if (thumbnailGalleryEl) {
      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        const thumbsSwiper = new Swiper(thumbnailGalleryEl, {
          ...recalculating,
          spaceBetween: 12,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          navigation: {
            prevEl: thumbnailWrapperEl?.querySelector('.swiper-button-prev') || thumbnailGalleryEl.querySelector('.swiper-button-prev'),
            nextEl: thumbnailWrapperEl?.querySelector('.swiper-button-next') || thumbnailGalleryEl.querySelector('.swiper-button-next'),
          },
          breakpoints: {
            768: {
              spaceBetween: 14,
            },
            1024: {
              spaceBetween: 16,
            },
          },
        });
        this.#swipers.push(thumbsSwiper);

        this.swiper = new Swiper(mainGalleryEl, {
          ...recalculating,
          spaceBetween: 0,
          thumbs: { swiper: thumbsSwiper },
        });
        this.#swipers.push(this.swiper);
      }
    }

    // Below the breakpoint the carousel is a native scroll-snap strip, exactly as
    // live builds it. Swiper is deliberately not attached: its drag handling is
    // what forced a click-and-drag instead of a flick, and an initialised Swiper
    // would translate the wrapper out from under the browser's own scrolling.
    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl && !this.carouselIsNative) {
      // No `pagination` module: live's PDP gallery has no dots at any width.
      this.swiper = new Swiper(carouselGalleryEl, {
        ...recalculating,
        slidesPerView: 'auto',
        spaceBetween: 5,
        direction: 'horizontal',
        navigation: {
          prevEl: carouselGalleryEl.querySelector('.swiper-button-prev'),
          nextEl: carouselGalleryEl.querySelector('.swiper-button-next'),
        },
      });
      this.#swipers.push(this.swiper);
    }

    this.initGalleryScrollbar(carouselGalleryEl);
  }

  /**
   * Progress bar for the native-scroll mobile gallery (gender-gated — see
   * sections/product.liquid's show_gallery_scrollbar). Live parity
   * (product.liquid's inline script): measures slide `offsetLeft` rather than
   * DOM order so it stays correct when slides 1/2 are CSS-reordered.
   * No explicit teardown: a colour swap replaces this markup wholesale, taking
   * the listener with it, same as live's re-run-per-swap approach.
   *
   * @param {Element|null} carouselGalleryEl
   * @returns {void}
   */
  initGalleryScrollbar(carouselGalleryEl) {
    // Re-run on every breakpoint change, so drop the previous listener first.
    this.#removeGalleryScroll?.();
    this.#removeGalleryScroll = null;

    const drag = this.querySelector('[data-product-gallery-scrollbar-drag]');
    const list = carouselGalleryEl?.querySelector('.swiper-wrapper');
    const slides = list?.querySelectorAll('.swiper-slide');
    if (!drag || !list || !slides || slides.length < 2) return;

    const total = slides.length;
    const dragPercent = Math.max(100 / total, 12);
    drag.style.width = `${dragPercent}%`;

    let ticking = false;
    const update = () => {
      ticking = false;
      const lefts = Array.from(slides)
        .map((slide) => slide.offsetLeft)
        .sort((a, b) => a - b);
      const step = lefts[1] - lefts[0];
      const index = step ? Math.round(list.scrollLeft / step) : 0;
      const progress = Math.min(Math.max(index / (total - 1), 0), 1);
      drag.style.left = `${progress * (100 - dragPercent)}%`;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    list.addEventListener('scroll', onScroll, { passive: true });
    this.#removeGalleryScroll = () => list.removeEventListener('scroll', onScroll);

    update();
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  get selectedOptionValues() {
    if (!this.variantSelector) return [];

    if (this.variantSelector.dataset.pickerType === 'dropdown') {
      return Array.from(this.variantSelector.querySelectorAll('select'))
        .map((select) => select.options[select.selectedIndex]?.dataset?.optionValueId)
        .filter(Boolean);
    }

    return Array.from(this.variantSelector.querySelectorAll('fieldset input:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    // Delegated from <product-info>, so ignore quantity / recipient-form inputs.
    if (!e.target.closest('variant-selector')) return;

    // Marks the first real option pick. Until this happens the color swatches
    // stay unslashed, which is what live does — see updateSiblingAvailability.
    this.dataset.variantSelectionMade = 'true';
    this.querySelector('[data-size-error]')?.style.setProperty('display', 'none');

    const productUrlChanged = e.target?.dataset?.productUrl ? e.target.dataset.productUrl !== this.dataset.url : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  onQuantitySelectorEvent(e) {
    const quantityInput = this.quantitySelector.querySelector('input[type="number"]');
    let currentValue = parseInt(quantityInput.value);
    const minValue = parseInt(quantityInput.getAttribute('min')) || 0;
    const maxValue = parseInt(quantityInput.getAttribute('max')) || Infinity;

    if (e.target.name === 'minus' && currentValue > minValue) {
      quantityInput.value = currentValue - 1;
    } else if (e.target.name === 'plus' && currentValue < maxValue) {
      quantityInput.value = currentValue + 1;
    } else if (e.type === 'change') {
      if (currentValue < minValue) {
        quantityInput.value = minValue;
      } else if (currentValue > maxValue) {
        quantityInput.value = maxValue;
      }
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;

    // Every layout is rendered at once and CSS reveals one of them, so move the
    // gallery that is actually on screen. Without the offsetParent guard this
    // matched the hidden stacked list on mobile and returned having done nothing.
    const stackedItem = this.querySelector(`.media-gallery__stacked-item[data-media-id="${variantFeaturedMediaId}"]`);
    if (stackedItem?.offsetParent) {
      stackedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
    if (!mediaSlide) return;

    const nativeScroller = this.carouselIsNative
      ? mediaSlide.closest('.product-media-gallery__carousel .swiper-wrapper')
      : null;
    if (nativeScroller) {
      // offsetLeft is measured against .swiper-wrapper, which Swiper's CSS keeps
      // position: relative even when no Swiper is attached.
      nativeScroller.scrollTo({ left: mediaSlide.offsetLeft, behavior: 'smooth' });
      return;
    }

    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex);
    this.swiper?.slideTo(slideIndex);
  }

  updateURL(variantId) {
    // Don't update URL if this is in a modal/quick-add context
    if (this.dataset.updateUrl === 'false') return;

    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState(
      { ...(window.history.state || {}), productSiblingHandle: this.dataset.url },
      '',
      `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`,
    );
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  updateVariantInputs(variantId) {
    const productForms = this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);

    for (const productForm of productForms) {
      const input = productForm.querySelector('input[name="id"]');
      if (input) input.value = variantId ?? '';
    }
  }

  /**
   * Liquid only outputs `checked` when product.selected_variant is set (?variant=).
   * Section fetches use option_values (selected_variant stays nil), so re-apply
   * the shopper's pick after swapping the variant-selector HTML.
   *
   * @param {string[]} optionValueIds
   * @returns {void}
   */
  applyOptionCheckedState(optionValueIds) {
    const selector = this.variantSelector;
    if (!selector || !optionValueIds?.length) return;

    if (selector.dataset.pickerType === 'dropdown') {
      for (const select of selector.querySelectorAll('select')) {
        for (const option of select.options) {
          if (optionValueIds.includes(option.dataset.optionValueId)) {
            option.selected = true;
            const legend = select.closest('fieldset')?.querySelector('[data-option-value]');
            if (legend) legend.textContent = option.textContent?.trim() || option.value;
            break;
          }
        }
      }
      return;
    }

    for (const id of optionValueIds) {
      const input = selector.querySelector(`input[data-option-value-id="${id}"]`);
      if (!input) continue;
      input.checked = true;
      const legend = input.closest('fieldset')?.querySelector('[data-option-value]');
      if (legend) legend.textContent = input.value;
    }
  }

  /**
   * The shopper's current picks as `{ "Size": "M" }`, taken from the picker DOM.
   * Color is skipped: with siblings it is a separate product, not a real option.
   *
   * @returns {Record<string, string>}
   */
  get selectedOptionsByName() {
    const selector = this.variantSelector;
    if (!selector) return {};

    const selected = {};
    for (const fieldset of selector.querySelectorAll('[data-option-name]')) {
      const name = fieldset.dataset.optionName;
      if (!name || name.toLowerCase().includes('color') || name.toLowerCase().includes('colour')) continue;

      const select = fieldset.querySelector('select');
      const value = select ? select.options[select.selectedIndex]?.value : fieldset.querySelector('input:checked')?.value;
      if (value) selected[name] = value;
    }

    return selected;
  }

  /**
   * Slash the color swatches that cannot serve the currently selected size.
   *
   * Live parity (product-swatch.js `updateColorSwatchAvailability` +
   * `hasMadeVariantSelection`): the slash is purely a "not in this size" signal.
   * A color that is sold out in every size shows no slash on page load or right
   * after a color swap — it only appears once the shopper picks a size, and only
   * on the colors that lack that size. Each color's own variant list comes from
   * the `data-sibling-variants` JSON, because colors are separate products.
   *
   * @returns {void}
   */
  updateSiblingAvailability() {
    const swatches = this.querySelectorAll('[data-sibling-variants]');
    if (!swatches.length) return;

    const selected = this.selectedOptionsByName;
    const selectedNames = Object.keys(selected);

    if (this.dataset.variantSelectionMade !== 'true' || !selectedNames.length) {
      for (const swatch of swatches) swatch.dataset.available = 'true';
      return;
    }

    for (const swatch of swatches) {
      let siblingVariants;
      try {
        siblingVariants = JSON.parse(swatch.dataset.siblingVariants);
      } catch (error) {
        continue;
      }

      const match = siblingVariants.find((siblingVariant) =>
        selectedNames.every((name) => siblingVariant.options?.[name] === selected[name]),
      );

      swatch.dataset.available = match?.available ? 'true' : 'false';
    }
  }

  /**
   * After an option_values section render, Liquid still thinks nothing is selected
   * (no ?variant=), so ATC HTML stays on "Select a Size/Amount". Sync the button
   * from the resolved variant JSON instead.
   *
   * @param {object | null} variant
   * @returns {void}
   */
  syncBuyButton(variant) {
    const container = this.querySelector(`#AddToCartContainer-${this.dataset.section}`);
    const buttonsWrap = this.querySelector('.product-form__buttons');
    if (!container || !buttonsWrap) return;

    const addLabel = buttonsWrap.dataset.atcAdd;
    const soldOutLabel = buttonsWrap.dataset.atcSoldOut;
    const addedLabel = buttonsWrap.dataset.atcAdded;
    const selectSizeLabel = buttonsWrap.dataset.atcSelectSize;
    const selectAmountLabel = buttonsWrap.dataset.atcSelectAmount;
    const buttonClass = buttonsWrap.dataset.atcButtonClass || 'button--primary';
    const isGiftCard = document.body.classList.contains('template--product-gift-card');

    if (!variant?.id) {
      const placeholderLabel = isGiftCard ? selectAmountLabel : selectSizeLabel;
      container.replaceChildren();
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `${buttonClass} product-form__placeholder`;
      button.innerHTML = `<span class="product-form__btn-text"></span>`;
      button.querySelector('.product-form__btn-text').textContent = placeholderLabel;
      container.append(button);
      return;
    }

    if (!variant.available) {
      container.replaceChildren();
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `${buttonClass} product-form__notify`;
      button.dataset.notifyTrigger = '';
      button.dataset.variantId = String(variant.id);
      button.textContent = soldOutLabel;
      container.append(button);
      return;
    }

    container.replaceChildren();
    const button = document.createElement('button');
    button.id = `AddToCart-${this.dataset.section}`;
    button.type = 'submit';
    button.name = 'add';
    button.className = buttonClass;
    /* No loader span, matching the server-rendered button — live's buy box goes
       straight from the label to the confirmation with nothing in between. */
    button.innerHTML = `
      <span class="product-form__btn-text"></span>
      <span class="product-form__btn-added" aria-hidden="true"></span>
    `;
    button.querySelector('.product-form__btn-text').textContent = addLabel;
    button.querySelector('.product-form__btn-added').textContent = addedLabel;
    container.append(button);
  }

  /**
   * Resolve and render a shopper's variant option selection.
   *
   * @param {boolean} productUrlChanged
   * @param {string} productUrl
   * @returns {Promise<void>}
   */
  async renderSection(productUrlChanged, productUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // Capture before the DOM is replaced (checked radios become the source of truth).
    const optionValueIds = this.selectedOptionValues;

    // If the section is in a modal, use the original section id without the modal suffix
    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;

    try {
      const response = await fetch(`${productUrl}?option_values=${optionValueIds}&section_id=${sectionId}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Unable to render product section: ${response.status}`);

      let responseText = await response.text();

      // If the section is in a modal, replace the original section id with the modal section id
      if (this.dataset.updateUrl === 'false') {
        responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
      }

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const variant = this.getSelectedVariant(html);

      if (productUrlChanged) {
        // Combined-listing / different product URL — full section swap, no size preselected.
        const productInfo = html.querySelector('product-info');
        if (!productInfo) return;

        if (this.dataset.updateUrl !== 'false') {
          window.history.pushState({ productSiblingHandle: productUrl }, '', productUrl);
          productInfo.dataset.currentHandle = new URL(productUrl, window.location.origin).pathname;
        }

        this.replaceWith(productInfo);
        ProductInfo.refreshCompanionSections(productUrl);
        return;
      }

      this.updateMedia(variant?.featured_media?.id);
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);
      this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
      this.applyOptionCheckedState(optionValueIds);
      this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
      this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
      // ATC from Liquid is still the unselected placeholder (no ?variant= on the
      // section request) — sync from the resolved variant instead.
      this.syncBuyButton(variant);
      // Runs after applyOptionCheckedState: it reads the picks back off the DOM.
      this.updateSiblingAvailability();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
    }
  }

  /**
   * External scripts arriving through DOMParser are inert. Re-inserting them as
   * fresh nodes lets a companion section load its custom elements when the
   * initial product did not render that section. Module URLs already evaluated
   * by the browser are not executed twice.
   *
   * @param {ParentNode} root
   * @returns {void}
   */
  static activateSectionScripts(root) {
    for (const script of root.querySelectorAll('script[src]')) {
      const replacement = document.createElement('script');
      for (const { name, value } of script.attributes) replacement.setAttribute(name, value);
      script.replaceWith(replacement);
    }
  }

  /**
   * Re-render one companion section against a different product handle.
   *
   * @param {HTMLElement} wrapper - the `#shopify-section-*` element
   * @param {string} productUrl
   * @returns {Promise<void>}
   */
  static async refreshCompanionSection(wrapper, productUrl) {
    const sectionId = wrapper.id.replace('shopify-section-', '');
    if (!sectionId) return;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.getElementById(`shopify-section-${sectionId}`);

      // No node back means the new product has nothing to show here (e.g. no
      // upsell metafield), which is also how a fresh page load would render it.
      wrapper.replaceChildren(...(next ? Array.from(next.childNodes) : []));
      ProductInfo.activateSectionScripts(wrapper);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Colors are separate products, so sections rendered from the product context
   * still describe the previous color after a sibling swap. Live refetches them
   * for the new handle (product-swatch.js `getOrFetchPairsWellWithSection`).
   *
   * @param {string} productUrl
   * @returns {void}
   */
  static refreshCompanionSections(productUrl) {
    for (const wrapper of document.querySelectorAll(`.${COMPANION_SECTION_CLASS}`)) {
      ProductInfo.refreshCompanionSection(wrapper, productUrl);
    }
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
